import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, MessageCircle, MoreHorizontal, Image, Video, Sparkles, Share2, Bell, MessageSquare, Wrench, List, Home as HomeIcon, ShoppingCart, Tag, Lightbulb, BookOpen, Megaphone, UserPlus, Send, ChevronRight, Shield } from 'lucide-react';
import api from '../utils/api';
import authStore from '../store/authStore';
import { ImageCarousel } from '../components/ImageCarousel';

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'adesso';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} or${hours === 1 ? 'a' : 'e'} fa`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} giorn${days === 1 ? 'o' : 'i'} fa`;
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

const PostImage = ({ src, alt = '' }) => {
  if (!src) return null;
  const isBase64 = src.startsWith('data:');
  return (
    <div className="relative bg-gray-100 overflow-hidden">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full object-contain max-h-[500px]"
        style={isBase64 ? { imageRendering: 'auto' } : {}}
      />
    </div>
  );
};

const CommentSection = ({ postId, commentsCount, onCountUpdate }) => {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const { user } = authStore();

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/posts/${postId}/comments`);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch {}
    finally { setLoading(false); }
  }, [postId]);

  useEffect(() => { if (open) fetchComments(); }, [open, fetchComments]);

  const handleSubmit = async () => {
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/api/posts/${postId}/comments`, { content: newComment.trim() });
      setComments(prev => [res.data, ...prev]);
      setNewComment('');
      if (onCountUpdate) onCountUpdate(postId, (commentsCount || 0) + 1);
    } catch {}
    finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} className="flex-1 flex items-center justify-center gap-2 py-2.5 text-gray-500 hover:bg-gray-50 transition" data-testid={`comment-toggle-${postId}`}>
        <MessageCircle className="w-5 h-5" />
        <span className="text-[13px] font-semibold">Commenta</span>
      </button>
      {open && (
        <div className="col-span-full border-t border-gray-100 px-4 py-3" data-testid={`comments-section-${postId}`}>
          {user && (
            <div className="flex items-center gap-2 mb-3">
              <input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} placeholder="Scrivi un commento..." className="flex-1 px-3 py-2 bg-gray-100 rounded-full text-sm outline-none" data-testid={`comment-input-${postId}`} />
              <button onClick={handleSubmit} disabled={!newComment.trim() || sending} className="w-8 h-8 rounded-full bg-[#10B981] flex items-center justify-center disabled:opacity-40" data-testid={`comment-send-${postId}`}><Send className="w-3.5 h-3.5 text-white" /></button>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-3"><div className="animate-spin rounded-full h-5 w-5 border-2 border-[#10B981] border-t-transparent" /></div>
          ) : comments.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Nessun commento</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {comments.map(c => (
                <div key={c.comment_id} className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {c.user_picture ? <img src={c.user_picture} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} /> : null}
                    <span className={`text-[10px] font-semibold text-gray-500 items-center justify-center w-full h-full ${c.user_picture ? 'hidden' : 'flex'}`}>{c.user_name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 rounded-xl px-3 py-2">
                      <p className="text-xs font-bold text-gray-900">{c.user_name}</p>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">{c.content}</p>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 ml-1">{timeAgo(c.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = authStore();
  const [posts, setPosts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [postMenu, setPostMenu] = useState(null);
  const [reportingPost, setReportingPost] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');

  const fetchFeed = useCallback(async () => {
    try {
      if (user) {
        const res = await api.get('/api/feed');
        const data = res.data;
        setPosts(data.posts || []);
        setPromotions(data.promotions || []);
        setRequests(data.requests || []);
        setSuggestedUsers(data.suggested_users || []);
      } else {
        const [promoRes, postsRes, reqRes] = await Promise.allSettled([
          api.get('/api/promotions'),
          api.get('/api/posts'),
          api.get('/api/requests?limit=5'),
        ]);
        if (promoRes.status === 'fulfilled') setPromotions(Array.isArray(promoRes.value.data) ? promoRes.value.data : promoRes.value.data?.promotions || []);
        if (postsRes.status === 'fulfilled') setPosts(Array.isArray(postsRes.value.data) ? postsRes.value.data : postsRes.value.data?.posts || []);
        if (reqRes.status === 'fulfilled') setRequests(Array.isArray(reqRes.value.data) ? reqRes.value.data : reqRes.value.data?.requests || []);
      }
    } catch (err) {
      console.error('Feed error:', err);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleLike = async (postId) => {
    if (!user) { navigate('/login'); return; }
    try {
      await api.post(`/api/posts/${postId}/like`);
      setLikedPosts(prev => {
        const s = new Set(prev);
        s.has(postId) ? s.delete(postId) : s.add(postId);
        return s;
      });
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, likes_count: (p.likes_count || 0) + (likedPosts.has(postId) || p.is_liked ? -1 : 1), is_liked: !(likedPosts.has(postId) || p.is_liked) } : p));
    } catch {}
  };

  const updateCommentCount = (postId, newCount) => {
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, comments_count: newCount } : p));
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Eliminare questo post?')) return;
    try { await api.delete(`/api/posts/${postId}`); setPosts(prev => prev.filter(p => p.post_id !== postId)); } catch {}
    setPostMenu(null);
  };

  const saveEditPost = async (postId) => {
    try { await api.put(`/api/posts/${postId}`, { content: editContent }); setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, content: editContent } : p)); setEditingPost(null); } catch {}
  };

  const reportPost = async (postId) => {
    try {
      await api.post('/api/reports', { content_type: 'post', content_id: postId, reason: reportReason });
      alert('Segnalazione inviata. L\'amministratore la esaminera.');
      setReportingPost(null);
      setReportReason('');
    } catch { alert('Errore'); }
  };

  const quickActions = [
    { icon: Wrench, label: 'Professionisti', path: '/search', bg: 'bg-blue-50', color: 'text-blue-600' },
    { icon: List, label: 'Servizi', path: '/services', bg: 'bg-red-50', color: 'text-red-600' },
    { icon: HomeIcon, label: 'Immobili', path: '/properties', bg: 'bg-amber-50', color: 'text-amber-600' },
    { icon: ShoppingCart, label: 'Marketplace', path: '/marketplace', bg: 'bg-emerald-50', color: 'text-emerald-600' },
    { icon: Tag, label: 'Promo', path: '/promotions', bg: 'bg-yellow-50', color: 'text-yellow-600' },
    { icon: BookOpen, label: 'Rivista', path: '/rivista', bg: 'bg-teal-50', color: 'text-teal-600' },
    { icon: Lightbulb, label: 'Ispirazioni', path: '/inspiration', bg: 'bg-purple-50', color: 'text-purple-600' },
    { icon: Megaphone, label: 'Richieste', path: '/requests', bg: 'bg-amber-50', color: 'text-amber-600' },
  ];

  if (loading) return <div className="min-h-screen bg-[#E5E7EB] flex items-center justify-center" data-testid="home-loading"><div className="animate-spin rounded-full h-10 w-10 border-2 border-[#10B981] border-t-transparent" /></div>;

  const isPaying = user?.has_full_access || user?.subscription_active || user?.trial_active;

  return (
    <div className="min-h-screen bg-[#E5E7EB]" data-testid="home-page">
      {/* Mobile Header */}
      <div className="md:hidden bg-[#1A1A2E] px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-white/10 p-0.5">
            <img src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png" alt="MakeHome 360" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-extrabold text-white">Make<span className="text-[#10B981]">Home</span><span className="text-white/60 text-sm ml-0.5">360</span></span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/notifications')} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="home-notifications-btn"><Bell className="w-5 h-5 text-[#F59E0B]" /></button>
          <button onClick={() => navigate('/messages')} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="home-messages-btn"><MessageSquare className="w-5 h-5 text-white" /></button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto lg:max-w-3xl">
        {/* Composer */}
        <div className="bg-white mb-2" data-testid="post-composer">
          <div className="p-4 flex items-center gap-3 cursor-pointer" onClick={() => user ? navigate('/post/create') : navigate('/login')}>
            <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} /> : null}
              <span className={`text-gray-500 font-semibold items-center justify-center w-full h-full ${user?.picture ? 'hidden' : 'flex'}`}>{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 border border-gray-200 rounded-full px-4 py-2.5"><span className="text-gray-400 text-sm">A cosa stai pensando?</span></div>
          </div>
          <div className="flex items-center border-t border-gray-100">
            <button className="flex-1 flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition" onClick={() => user ? navigate('/post/create') : navigate('/login')}><Image className="w-[18px] h-[18px] text-[#10B981]" /><span className="text-[13px] font-semibold text-gray-500">Foto</span></button>
            <div className="w-px h-5 bg-gray-200" />
            <button className="flex-1 flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition" onClick={() => user ? navigate('/post/create') : navigate('/login')}><Video className="w-[18px] h-[18px] text-blue-600" /><span className="text-[13px] font-semibold text-gray-500">Video</span></button>
            <div className="w-px h-5 bg-gray-200" />
            <button className="flex-1 flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition" onClick={() => user ? navigate('/post/create') : navigate('/login')}><Sparkles className="w-[18px] h-[18px] text-[#F59E0B]" /><span className="text-[13px] font-semibold text-gray-500">Ispirazione</span></button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white mb-2 py-3" data-testid="quick-actions">
          <div className="flex gap-4 overflow-x-auto px-4 scrollbar-hide">
            {quickActions.map((a, i) => (
              <button key={i} onClick={() => navigate(a.path)} className="flex flex-col items-center gap-1.5 flex-shrink-0">
                <div className={`w-[52px] h-[52px] rounded-2xl flex items-center justify-center ${a.bg}`}><a.icon className={`w-[22px] h-[22px] ${a.color}`} /></div>
                <span className="text-[11px] font-semibold text-gray-600">{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Admin Quick Access - small link, not prominent */}

        {/* For Pro users: Richieste Suggerite / For Private: Offerte */}
        {user && isPaying && requests.length > 0 ? (
          <div className="bg-white mb-2 py-3" data-testid="suggested-requests-section">
            <div className="flex items-center justify-between px-4 mb-3">
              <div className="flex items-center gap-2"><Megaphone className="w-[18px] h-[18px] text-amber-500" /><h3 className="text-base font-bold text-gray-900">Richieste suggerite</h3></div>
              <button onClick={() => navigate('/requests')} className="text-sm font-semibold text-blue-600">Vedi tutte</button>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">
              {requests.slice(0, 5).map(req => (
                <div key={req.request_id} onClick={() => navigate('/requests')} className="flex-shrink-0 w-64 bg-gray-50 rounded-2xl p-4 cursor-pointer hover:shadow-md transition border border-gray-100" data-testid={`home-request-${req.request_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-amber-50 text-[#F59E0B] text-[11px] font-bold px-2 py-0.5 rounded">{req.macro_category_it || req.macro_category || 'Richiesta'}</span>
                    {req.status === 'open' && <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-green-50 text-green-600">Aperta</span>}
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">{req.title}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">{req.description}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    {req.city && <span>{req.city}</span>}
                    {req.budget_max && <span className="text-[#10B981] font-bold">€{req.budget_max}</span>}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); navigate('/requests'); }} className="mt-2 w-full py-2 bg-[#10B981] text-white text-xs font-bold rounded-lg">Candidati</button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Promotions Carousel - visible for private users or when available */}
        {promotions.length > 0 && (
          <div className="bg-white mb-2 py-3" data-testid="promos-carousel">
            <div className="flex items-center justify-between px-4 mb-3">
              <div className="flex items-center gap-2"><Megaphone className="w-[18px] h-[18px] text-[#F59E0B]" /><h3 className="text-base font-bold text-gray-900">Offerte per te</h3></div>
              <button onClick={() => navigate('/promotions')} className="text-sm font-semibold text-blue-600">Vedi tutte</button>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">
              {promotions.slice(0, 6).map(promo => (
                <div key={promo.promotion_id || promo.promo_id} onClick={() => navigate('/promotions')} className="flex-shrink-0 w-44 bg-gray-50 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition">
                  <div className="h-24 bg-amber-50 flex items-center justify-center">
                    {promo.photos?.[0] ? <img src={promo.photos[0]} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Tag className="w-7 h-7 text-amber-400" />}
                  </div>
                  <div className="p-2.5">
                    {promo.discount_type === 'percentage' && <span className="inline-block bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-md mb-1">-{promo.discount_value}%</span>}
                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2">{promo.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{promo.business_name || promo.user_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Users - ALWAYS visible when available, CLICKABLE */}
        {suggestedUsers.length > 0 && (
          <div className="bg-white mb-2 py-3" data-testid="suggested-users">
            <div className="flex items-center gap-2 px-4 mb-3">
              <UserPlus className="w-[18px] h-[18px] text-blue-600" />
              <h3 className="text-base font-bold text-gray-900">Persone che potresti conoscere</h3>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">
              {suggestedUsers.map(u => (
                <div key={u.user_id} className="flex-shrink-0 w-32 bg-gray-50 rounded-2xl py-4 px-3 flex flex-col items-center">
                  <div onClick={() => u.professional_id ? navigate(`/professionals/${u.professional_id}`) : navigate(`/search`)} className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mb-2 cursor-pointer hover:ring-2 hover:ring-[#10B981]">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" loading="lazy" /> : <span className="text-gray-500 font-semibold">{u.name?.charAt(0)}</span>}
                  </div>
                  <p onClick={() => u.professional_id ? navigate(`/professionals/${u.professional_id}`) : null} className="text-sm font-semibold text-gray-900 text-center truncate w-full cursor-pointer">{u.name}</p>
                  {u.category && <p className="text-[11px] text-gray-400 truncate w-full text-center">{u.category}</p>}
                  <button onClick={async () => {
                    if (!user) { navigate('/login'); return; }
                    try { await api.post(`/api/users/${u.user_id}/follow`); setSuggestedUsers(prev => prev.filter(s => s.user_id !== u.user_id)); } catch {}
                  }} className="mt-2 flex items-center gap-1 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`follow-btn-${u.user_id}`}><UserPlus className="w-3 h-3" />Segui</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Requests Section - for non-pro users */}
        {requests.length > 0 && !(user && isPaying) && (
          <div className="bg-white mb-2 py-3" data-testid="requests-section">
            <div className="flex items-center justify-between px-4 mb-3">
              <div className="flex items-center gap-2"><Megaphone className="w-[18px] h-[18px] text-amber-500" /><h3 className="text-base font-bold text-gray-900">Richieste di lavoro</h3></div>
              <button onClick={() => navigate('/requests')} className="text-sm font-semibold text-blue-600">Vedi tutte</button>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">
              {requests.slice(0, 5).map(req => (
                <div key={req.request_id} onClick={() => navigate('/requests')} className="flex-shrink-0 w-64 bg-gray-50 rounded-2xl p-4 cursor-pointer hover:shadow-md transition border border-gray-100" data-testid={`home-request-${req.request_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-amber-50 text-[#F59E0B] text-[11px] font-bold px-2 py-0.5 rounded">{req.macro_category_it || req.macro_category || 'Richiesta'}</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">{req.title}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2">{req.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Posts */}
        {posts.map(post => {
          const isLiked = likedPosts.has(post.post_id) || post.is_liked;
          return (
            <div key={post.post_id} className="bg-white mb-2" data-testid={`post-${post.post_id}`}>
              <div className="p-4 pb-2">
                <div className="flex items-center gap-3 mb-3">
                  {/* CLICKABLE profile icon */}
                  <div onClick={() => post.professional_id ? navigate(`/professionals/${post.professional_id}`) : null} className={`w-12 h-12 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0 ${post.professional_id ? 'cursor-pointer hover:ring-2 hover:ring-[#10B981]' : ''}`} data-testid={`post-avatar-${post.post_id}`}>
                    {(post.user_picture || post.author_picture) ? (
                      <img src={post.user_picture || post.author_picture} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    ) : null}
                    <span className={`text-gray-500 font-semibold items-center justify-center w-full h-full ${(post.user_picture || post.author_picture) ? 'hidden' : 'flex'}`}>{(post.user_name || post.author_name)?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span onClick={() => post.professional_id ? navigate(`/professionals/${post.professional_id}`) : null} className={`font-bold text-[15px] text-gray-900 ${post.professional_id ? 'cursor-pointer hover:text-[#10B981]' : ''}`} data-testid={`post-author-${post.post_id}`}>{post.user_name || post.author_name || 'Utente'}</span>
                      {post.is_official && <span className="inline-flex items-center gap-0.5 bg-slate-700 text-white text-[8px] font-bold px-1.5 py-0.5 rounded ml-1">System admin</span>}
                      {post.is_professional && !post.is_official && <span className="text-[10px] font-bold bg-[#10B981] text-white px-1.5 py-0.5 rounded">Pro</span>}
                    </div>
                    <span className="text-xs text-gray-400">{timeAgo(post.created_at)}</span>
                  </div>
                  <button onClick={() => setPostMenu(postMenu === post.post_id ? null : post.post_id)} className="p-2 hover:bg-gray-100 rounded-full relative" data-testid={`post-menu-${post.post_id}`}><MoreHorizontal className="w-5 h-5 text-gray-400" />
                    {postMenu === post.post_id && (
                      <div className="absolute right-0 top-full mt-1 bg-white shadow-xl rounded-xl border border-gray-100 py-1 w-40 z-50" data-testid={`post-menu-dropdown-${post.post_id}`}>
                        {post.user_id === user?.user_id && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); setEditingPost(post.post_id); setEditContent(post.content || ''); setPostMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Modifica</button>
                            <button onClick={(e) => { e.stopPropagation(); deletePost(post.post_id); }} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">Elimina</button>
                          </>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); setReportingPost(post.post_id); setPostMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm text-amber-600 hover:bg-amber-50" data-testid={`report-post-${post.post_id}`}>Segnala</button>
                      </div>
                    )}
                  </button>
                </div>
                {editingPost === post.post_id ? (
                  <div className="mb-2">
                    <textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="w-full p-3 bg-gray-50 rounded-xl text-sm border border-gray-200 outline-none resize-none min-h-[60px]" data-testid={`edit-post-input-${post.post_id}`} />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => saveEditPost(post.post_id)} className="px-4 py-1.5 bg-[#10B981] text-white text-sm font-semibold rounded-lg" data-testid={`save-edit-${post.post_id}`}>Salva</button>
                      <button onClick={() => setEditingPost(null)} className="px-4 py-1.5 bg-gray-100 text-gray-600 text-sm font-semibold rounded-lg">Annulla</button>
                    </div>
                  </div>
                ) : post.content ? <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line mb-2">{post.content}</p> : null}
                {reportingPost === post.post_id && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2" data-testid={`report-form-${post.post_id}`}>
                    <p className="text-xs font-bold text-amber-700 mb-1">Segnala questo post all'amministratore</p>
                    <input value={reportReason} onChange={e => setReportReason(e.target.value)} placeholder="Motivo della segnalazione..." className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm outline-none mb-2" />
                    <div className="flex gap-2">
                      <button onClick={() => reportPost(post.post_id)} className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg">Invia segnalazione</button>
                      <button onClick={() => { setReportingPost(null); setReportReason(''); }} className="px-3 py-1.5 bg-white text-gray-500 text-xs font-semibold rounded-lg">Annulla</button>
                    </div>
                  </div>
                )}
                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {post.tags.map((tag, i) => <span key={i} className="text-[13px] text-blue-600 font-semibold">#{tag}</span>)}
                  </div>
                )}
              </div>
              {/* Image carousel */}
              {post.photos?.length > 0 && (
                <ImageCarousel photos={post.photos} />
              )}
              <div className="px-4 py-2 flex justify-between text-xs text-gray-400 border-b border-gray-100">
                <span>{post.likes_count > 0 && `${post.likes_count} mi piace`}</span>
                <span>{post.comments_count > 0 && `${post.comments_count} commenti`}</span>
              </div>
              <div className="flex flex-wrap">
                <button onClick={() => handleLike(post.post_id)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition ${isLiked ? 'text-red-500' : 'text-gray-500 hover:bg-gray-50'}`} data-testid={`like-btn-${post.post_id}`}>
                  <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500' : ''}`} />
                  <span className="text-[13px] font-semibold">Mi piace</span>
                </button>
                <CommentSection postId={post.post_id} commentsCount={post.comments_count} onCountUpdate={updateCommentCount} />
              </div>
            </div>
          );
        })}

        {posts.length === 0 && suggestedUsers.length === 0 && requests.length === 0 && (
          <div className="bg-white p-12 text-center" data-testid="empty-feed">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3"><Megaphone className="w-6 h-6 text-gray-300" /></div>
            <p className="font-semibold text-gray-400">Il feed è vuoto</p>
            <p className="text-sm text-gray-300 mt-1">Segui altri utenti per vedere i loro post</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
;
